import debounce from 'debounce';
import * as React from 'react';
import { connect, Dispatch } from 'react-redux';
import { parse } from 'query-string';
import l10n from '@built/i18n';

import Auth from '../components/Auth';
import {
  AuthData,
  AuthError,
  BrandingData,
  StoreState,
  AuthErrorType
} from '../types';
import {
  handleAuthenticatedUser,
  login as _login,
  setEmailAddress as _setEmailAddress,
  setInitialPassword as _setInitialPassword,
  setAuthError as _setAuthError,
  handleLogout,
  showToast,
  ToastOpts,
  trackSnowplowEvent,
  SnowplowOpts
} from '../actions';
import {
  getAuthData,
  getAuthError,
  getBranding,
  getExternalLoginUrl
} from '../reducers';
import { validateEmail } from './EmailField';
import { getLanguageReady } from 'src/reducers/i18n';

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  /**
   * This class maintains some state for the underlying Auth component,
   * and provides callbacks to update this state as well as to log the user
   * in with the appropriate credentials. It also toggles on and off the busy state
   * while authentication is happening.
   */
  class AuthContainer extends React.Component<
    AuthContainerProps,
    AuthContainerState
  > {
    constructor(props: AuthContainerProps) {
      super(props);
      const params = parse(location.search);

      let email = '';
      let emailValid = false;
      if (params.email) {
        // convert provided email to lowercase (LOGIN-144)
        email = params.email.toLowerCase();
        emailValid = validateEmail(email);
      }

      this.state = {
        code: params.code || '',
        email: email,
        emailValid: emailValid,
        remember: false,
        password: '',
        busy: false,
        showEmailError: false,
        showPasswordError: false
      };

      // LOGIN-168 switching to onChange from onBlur causes
      // any valid email address to be set in state each
      // time the input changes. Since we really don't want
      // this to happen so often, we wrap the method using
      // debounce
      // LOGIN-180 originally we debounced the email address
      // change event handler method, but that allowed users
      // to submit the form with a previously typed email.
      // We're now debouncing the state action method for
      // setting the email address
      this.setEmailAddress = debounce(this.setEmailAddress, 500);
    }

    render() {
      const {
        authData,
        authError,
        branding,
        className,
        externalLoginUrl,
        logout
      } = this.props;
      const {
        code,
        busy,
        remember,
        email,
        emailValid,
        password,
        showEmailError,
        showPasswordError
      } = this.state;
      return (
        <Auth
          branding={branding}
          externalLoginUrl={externalLoginUrl}
          className={className}
          authData={authData}
          authError={authError}
          email={email}
          emailValid={emailValid}
          showEmailError={showEmailError}
          showPasswordError={showPasswordError}
          password={password}
          login={this.login}
          code={code}
          busy={busy}
          remember={remember}
          logout={logout}
          sendMfaCode={this.handleSubmitCode}
          onResendSmsCode={this.handleResend}
          setInitialPassword={this.handleSetPassword}
          onWelcomeBackSubmit={this.handleWelcomeBack}
          onInputChange={this.handleInputChange}
          onEmailAddressChange={this.handleEmailAddressChange}
          trackForgotPassword={this.trackForgotPassword}
          trackLoginSSO={this.trackLoginSSO}
        />
      );
    }

    login = (event: React.SyntheticEvent<HTMLElement>) => {
      event.preventDefault();

      const snowplowOpts = {
        eventCategory: 'login',
        eventAction: 'login',
        eventContext: 'login',
        eventId: null
      };

      this.props.trackSnowplowEvent(snowplowOpts);

      if (!this.validateLogin()) {
        return;
      }

      this.setState({ ...this.state, busy: true });
      this.props.login(this.state.email, this.state.password).then(success => {
        if (!success) {
          this.setState({ ...this.state, busy: false });
        } else if (!this.props.authData.needsMfa) {
          // login successful, attempt to redirect
          this.redirectUser();
        } else {
          this.setState({ busy: false });
        }
      });
    };

    // wrapper around redirectUser to make sure we immediately redirect
    // when a user clicks the continue button
    handleWelcomeBack = (event: React.SyntheticEvent<HTMLElement>) => {
      this.redirectUser(event, true);
    };

    redirectUser = (
      event?: React.SyntheticEvent<HTMLElement>,
      immediate = false
    ) => {
      if (event) {
        event.preventDefault();
      }

      this.setState({ ...this.state, busy: true });
      this.props.redirectUser(immediate);
    };

    handleSubmitCode = (event: React.SyntheticEvent<HTMLElement>) => {
      event.preventDefault();

      // check for missing code and bail out if needed
      if (this.state.code === '') {
        this.props.setAuthError({
          type: AuthErrorType.MFA_VERIFICATION_ERROR,
          message: l10n.t(
            'Auth:Mfa.errors.missingVerificationCode',
            'Please provide a verification code.'
          )
        });
        return;
      }

      this.setState({ busy: true });
      this.props
        .sendMfaCode(this.state.code, this.state.remember)
        .then(success => {
          this.setState({ busy: false });
          if (!success) {
            const curError = this.props.authError || null;

            this.props.setAuthError({
              type: AuthErrorType.MFA_VERIFICATION_ERROR,
              message: curError ? curError.message : ''
            });
          } else {
            this.redirectUser();
          }
        })
        .catch(e => {
          // tslint:disable-next-line
          console.log(e);
        });
    };

    handleResend = (event: React.SyntheticEvent<HTMLElement>) => {
      event.preventDefault();
      this.setState({ busy: true });

      this.props.login(this.state.email, this.state.password).then(success => {
        this.setState({ busy: false });
        if (!success) {
          this.props.setAuthError({
            type: AuthErrorType.MFA_RESEND_SMS_ERROR,
            message: ''
          });
        } else {
          const authData = this.props.authData || {};
          const len = authData.mfaParams.CODE_DELIVERY_DESTINATION.length;
          const last4 = authData.mfaParams.CODE_DELIVERY_DESTINATION.substring(
            len - 4
          );

          this.props.showToast(
            l10n.t('SetupMfa:toaster.resendVerificationCodeMessage', {
              defaultValue:
                'A new verification code was sent to the number ending in {{last4}}.',
              last4
            })
          );
        }
      });
    };

    handleSetPassword = (event: React.SyntheticEvent<HTMLElement>) => {
      event.preventDefault();
      this.setState({ busy: true });
      this.props.setInitialPassword(this.state.password).then(success => {
        if (!success) {
          this.setState({ busy: false });
        }
      });
    };

    handleInputChange = (event: React.SyntheticEvent<HTMLInputElement>) => {
      event.preventDefault();
      const name = event.currentTarget.name;

      if (name === 'remember') {
        const checked = event.currentTarget.checked;
        this.setState({ ...this.state, [name]: checked });
      } else if (name === 'password') {
        // LOGIN-168 reset password input error state
        this.setState({
          ...this.state,
          password: event.currentTarget.value,
          showPasswordError: false
        });
      } else if (name === 'code') {
        // LOGIN-213 when a code changes we should clear the validation
        // error if needed
        const code = event.currentTarget.value;
        if (this.props.authError && code !== '') {
          this.props.setAuthError();
        }
        this.setState({ code });
      } else {
        const value = event.currentTarget.value;
        this.setState({ ...this.state, [name]: value });
      }
    };

    handleEmailAddressChange = (email: string, emailValid: boolean) => {
      this.setState({
        email,
        emailValid,
        showEmailError: false
      });

      if (emailValid) {
        this.setEmailAddress(email);
      }
    };

    /**
     * Mainly useful for debouncing the call to
     * setEmailAddress action to update state
     */
    setEmailAddress = (email: string) => {
      this.props.setEmailAddress(email);
    };

    /**
     *
     */
    validateLogin = (): boolean => {
      const errorState = { showEmailError: false, showPasswordError: false };

      if (!this.state.email || !this.state.emailValid) {
        errorState.showEmailError = true;
      }

      if (!this.state.password) {
        errorState.showPasswordError = true;
      }

      this.setState({ ...this.state, ...errorState });
      return !(errorState.showEmailError || errorState.showPasswordError);
    };

    /**
     * Track snowplow event when a user
     * clicks on "Forgot Password"
     */
    trackForgotPassword = (): void => {
      const snowplowOpts = {
        eventCategory: 'login',
        eventAction: 'forgot-password',
        eventContext: 'login-page',
        eventId: null
      };

      this.props.trackSnowplowEvent(snowplowOpts);
    };

    /**
     * Track snowplow event when a user
     * clicks "Login with SSO"
     */
    trackLoginSSO = (): void => {
      const snowplowOpts = {
        eventCategory: 'login',
        eventAction: 'login-SSO',
        eventContext: 'login-page',
        eventId: null
      };

      this.props.trackSnowplowEvent(snowplowOpts);
    };
  }
);

export interface AuthContainerProps {
  /** Current data for the currently authorized user */
  authData: AuthData;
  /** An error to display */
  authError?: AuthError;
  /** An optional className to override styles */
  className?: string;
  /** Optional branding to customize the UI */
  branding?: BrandingData;
  /** Where to send the browser to for external auth */
  externalLoginUrl?: string;
  /** Flag to trigger re-render when language is ready */
  languageReady: boolean;
  /** Callback to log the user in */
  login(username: string, password: string): Promise<boolean>;
  logout(): void;
  /** Callback to redirect already authenticated users to console */
  redirectUser(immediate: boolean): void;
  /** Callback to submit an MFA code to login */
  sendMfaCode(code: string, remember?: boolean): Promise<boolean>;
  /** Callback to set the app's auth error state */
  setAuthError(authError?: AuthError): void;
  /** Callback to set a user's email address */
  setEmailAddress(email?: string): void;
  /** Callback to set a user's initial password */
  setInitialPassword(password: string): Promise<boolean>;
  /** Callback to show a toast */
  showToast(message: string, opts?: ToastOpts): void;
  /** Callback to track an event to snowplow */
  trackSnowplowEvent(opts: SnowplowOpts): void;
}

export interface AuthContainerState {
  /** The current value of the email address */
  email: string;

  /** Whether current email is valid */
  emailValid: boolean;

  /** The current value of the password */
  password: string;

  /** The current value of the MFA code */
  code: string;

  /** Whether to remember this device */
  remember: boolean;

  /** Whether to indicate that the application is processing */
  busy: boolean;

  /** */
  showEmailError: boolean;
  showPasswordError: boolean;
}

function mapStateToProps(
  state: StoreState,
  _ownProps?: Pick<AuthContainerProps, 'className'>
): Pick<
  AuthContainerProps,
  'authData' | 'authError' | 'branding' | 'externalLoginUrl' | 'languageReady'
> {
  return {
    externalLoginUrl: getExternalLoginUrl(state),
    authData: getAuthData(state),
    authError: getAuthError(state),
    branding: getBranding(state),
    languageReady: getLanguageReady(state)
  };
}

function mapDispatchToProps(
  dispatch: Dispatch<StoreState>
): Pick<
  AuthContainerProps,
  | 'login'
  | 'logout'
  | 'redirectUser'
  | 'setAuthError'
  | 'setEmailAddress'
  | 'setInitialPassword'
  | 'sendMfaCode'
  | 'showToast'
  | 'trackSnowplowEvent'
> {
  return {
    login(username, password) {
      return dispatch(_login({ username, password }));
    },
    logout() {
      return handleLogout(dispatch);
    },
    redirectUser(immediate: boolean) {
      return dispatch(handleAuthenticatedUser(immediate));
    },
    setAuthError(authError: AuthError) {
      return dispatch(_setAuthError(authError));
    },
    setEmailAddress(email: string) {
      return dispatch(_setEmailAddress(email));
    },
    setInitialPassword(newPassword) {
      return dispatch(_setInitialPassword(newPassword));
    },
    sendMfaCode(code: string, remember?: boolean) {
      return dispatch(_login({ code, rememberDevice: remember }));
    },
    showToast(message: string, opts?: ToastOpts) {
      return dispatch(showToast(message, opts));
    },
    trackSnowplowEvent(opts: SnowplowOpts) {
      return dispatch(trackSnowplowEvent(opts));
    }
  };
}



// WEBPACK FOOTER //
// ./src/containers/Auth.tsx